import { configureRequestParams, getErrorMessage } from '../utils/helpers'

import axios from './instance'

/**
 * Gets the certificates for an entity
 * @param {string} entity - The entity id to get certificates for.
 * @param {import('../utils/helpers').RequestParams} params - The request parameters including sorting, pagination, searchTerm
 * */
export const getEntityStudentCertificates = async (
  entity,
  { sorting, pagination, searchTerm, filters },
) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(
      `/entities/${entity}/student-certificates/${params}&expand=course_requirements.primary_course,course_requirements.alternative_courses`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
/**
 * Gets the students assigned to a certificate for an entity
 * @param {string} entity - The entity id to get certificates for.
 * @param {string} certificate - The certificate id to get students for.
 * @param {import('../utils/helpers').RequestParams} params - The request parameters including sorting, pagination, searchTerm
 * */
export const getEntityCertificateStudents = async (
  entity,
  certificate,
  { sorting, pagination, searchTerm, filters },
) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, {
    ...filters,
    certificate,
  })

  try {
    return await axios.get(
      `/entities/${entity}/student-certificates/${params}&expand=certificate.training,course_records.course,student.user,event,event.certificates,assigned_by`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
/**
 * Gets the certificates assigned to a student for an entity
 * @param {object} options
 * @param {string} options.entityId - The entity id to get certificates for.
 * @param {string} options.studentId - The entity role id to get certificates for.
 * */
export const getStudentEntityCertificates = async ({ entityId, studentId }) => {
  try {
    // This is the student's full "My Courses" list — every certificate renders
    // as a card and feeds the per-tab counts, so we pull all of them rather than
    // the default 20-row page (limit follows the codebase's fetch-all convention).
    return await axios.get(
      `/entities/${entityId}/student-certificates/?expand=certificate,certificate.training,student,student.user,course_records.course,document,event,event.certificates,event.roster&student=${studentId}&limit=1000`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Assign a student to a certificate.
 * @param {object} payload
 * @param {string} payload.entity - The entity id.
 * @param {object} [payload.newUserData] - If creating a new user, provide firstName, lastName, email.
 * @param {string} [payload.existingUser] - The user id to assign the student to. Only provide one of newUserData or existingUser.
 * @param {string[]} payload.courses - The courses the student is assigned to.
 * @param {string} payload.certificateId - The certificate id to assign the student to.
 */
export const assignStudentCertificate = async ({
  entity,
  newUserData,
  existingUser,
  certificateId,
  courses,
}) => {
  const payload = {
    newUserData,
    existingUser,
    certificate: certificateId,
    courses,
  }
  try {
    return await axios.post(`/entities/${entity}/student-certificates/`, payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Remove a student from a certificate and refund the credit cost.
 * @param {string} entityId - The entity id.
 * @param {string} studentCertificateId - The student certificate id to remove.
 */
export const removeStudentCertificate = async (entityId, studentCertificateId) => {
  try {
    return await axios.delete(
      `/entities/${entityId}/student-certificates/${studentCertificateId}/`,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

export const grantStudentCertificate = async (
  entityId,
  studentCertificateId,
  proficiencyQuestions,
) => {
  try {
    return await axios.post(
      `/entities/${entityId}/student-certificates/${studentCertificateId}/issue/`,
      proficiencyQuestions,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
